/**
 * InteractiveVoiceService - AWS Nova 2 Sonic Integration
 * 
 * Handles real-time speech-to-speech conversations using AWS Nova 2 Sonic model
 * through Socket.IO proxy server.
 * 
 * Supports:
 * - Browser-based microphone input
 * - Real-time AI audio response
 * - Interrupt handling
 * - Live transcription
 */

import io from "socket.io-client";

// Backend URLs for different providers
const PROVIDERS = {
  AWS: {
    name: "AWS Nova 2 Sonic",
    proxyUrl: "https://nova-sonic-proxy-368345911172.us-central1.run.app",
    phoneUrl: "https://nova-v2-server-1015471535502.us-central1.run.app",
    voices: [
      { id: "tiffany", name: "Tiffany", gender: "Female", icon: "👩" },
      { id: "matthew", name: "Matthew", gender: "Male", icon: "👨" },
    ],
    badge: "Nova 2 Sonic",
    badgeColor: "orange",
  },
  GCP: {
    name: "Google Cloud (Coming Soon)",
    proxyUrl: null, // Will be added later
    phoneUrl: null,
    voices: [
      { id: "en-US-Journey-F", name: "Journey (Female)", gender: "Female", icon: "👩" },
      { id: "en-US-Journey-D", name: "Journey (Male)", gender: "Male", icon: "👨" },
    ],
    badge: "Gemini Live",
    badgeColor: "blue",
    disabled: true,
  },
};

// Default prompts for different modes
const DEFAULT_PROMPTS = {
  verification: `You are Ava, an AI Virtual Assistant from First Advantage conducting an employment verification call.

YOUR TASK:
1. Introduce yourself: "Hi, this is Ava, an AI assistant from First Advantage calling about an employment verification."
2. Ask to speak with HR or someone who can verify employment
3. Verify the following about the candidate:
   - Employment start and end dates
   - Job title or position held
   - Employment status (full-time, part-time, contractor)

BEHAVIOR RULES:
- Be professional, warm, and efficient
- Keep responses SHORT - 1 to 2 sentences maximum
- If you reach voicemail, leave a callback message with your return number
- If you reach an IVR, navigate to HR or press 0 for an operator
- If asked to call back, politely note the best time and thank them
- If they cannot verify, ask for an alternative contact

IMPORTANT:
- If interrupted while speaking, STOP immediately and listen
- Match the language of the person you're speaking with (English or Spanish)
- Be patient with IVR systems`,

  realtime: `You are a friendly, professional AI phone assistant having a natural conversation.

BEHAVIOR:
- Keep responses SHORT - 1 to 2 sentences maximum
- If the caller speaks while you're talking, STOP immediately and listen
- Be conversational and natural, not robotic
- Match the caller's language (English or Spanish)

Start by greeting the caller warmly.`,

  sales: `You are an AI Sales Development Representative following up on a previous email.

BEHAVIOR:
- Keep responses SHORT - 1 to 2 sentences maximum
- Be professional but friendly
- If interrupted, STOP immediately and listen
- Your goal is to schedule a meeting or get a referral

Start by introducing yourself and referencing the email you sent.`,

  custom: "", // User-defined prompt
};

class InteractiveVoiceService {
  constructor() {
    this.socket = null;
    this.audioContext = null;
    this.playbackContext = null;
    this.mediaStream = null;
    this.processor = null;
    this.audioQueue = [];
    this.isActive = false;
    this.nextPlayTime = 0;
    this.activeAudioSources = [];
    this.provider = "AWS";
    this.callbacks = {};
  }

  /**
   * Get available providers
   */
  static getProviders() {
    return PROVIDERS;
  }

  /**
   * Get available voices for a provider
   */
  static getVoices(provider = "AWS") {
    return PROVIDERS[provider]?.voices || [];
  }

  /**
   * Get default prompts
   */
  static getDefaultPrompts() {
    return DEFAULT_PROMPTS;
  }

  /**
   * Set event callbacks
   */
  on(event, callback) {
    this.callbacks[event] = callback;
  }

  /**
   * Emit event to callbacks
   */
  emit(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event](data);
    }
  }

  /**
   * Convert Float32Array to Int16Array for transmission
   */
  float32ToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return int16Array;
  }

  /**
   * Convert Int16Array to Float32Array for playback
   */
  int16ToFloat32(int16Array) {
    const float32Array = new Float32Array(int16Array.length);
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 0x8000;
    }
    return float32Array;
  }

  /**
   * Play audio from queue
   */
  async playAudioFromQueue() {
    if (this.audioQueue.length === 0) return;

    try {
      if (!this.playbackContext || this.playbackContext.state === "closed") {
        this.playbackContext = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 24000,
        });
        this.nextPlayTime = 0;
        this.activeAudioSources = [];
      }

      if (this.playbackContext.state === "suspended") {
        await this.playbackContext.resume();
      }

      const ctx = this.playbackContext;

      while (this.audioQueue.length > 0) {
        const audioData = this.audioQueue.shift();
        const int16Array = new Int16Array(audioData);
        const float32Data = this.int16ToFloat32(int16Array);

        const audioBuffer = ctx.createBuffer(1, float32Data.length, 24000);
        audioBuffer.copyToChannel(float32Data, 0);

        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);

        const startTime = Math.max(ctx.currentTime, this.nextPlayTime);
        source.start(startTime);
        this.nextPlayTime = startTime + audioBuffer.duration;

        this.activeAudioSources.push(source);
        source.onended = () => {
          this.activeAudioSources = this.activeAudioSources.filter(s => s !== source);
        };
      }
    } catch (err) {
      console.error("[InteractiveVoice] Playback error:", err);
    }
  }

  /**
   * Stop all audio playback (for interrupts)
   */
  stopAllAudio() {
    console.log("[InteractiveVoice] Stopping all audio - interrupt");
    
    this.activeAudioSources.forEach((source) => {
      try { source.stop(); } catch (e) {}
    });
    this.activeAudioSources = [];
    this.audioQueue = [];
    this.nextPlayTime = 0;
    
    if (this.playbackContext && this.playbackContext.state !== "closed") {
      this.playbackContext.close().catch(() => {});
      this.playbackContext = null;
    }
    
    this.emit("aiSpeakingChange", false);
  }

  /**
   * Setup socket event listeners
   */
  setupSocketListeners() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("[InteractiveVoice] Connected:", this.socket.id);
      this.emit("connected", { socketId: this.socket.id });
    });

    this.socket.on("disconnect", () => {
      console.log("[InteractiveVoice] Disconnected");
      this.isActive = false;
      this.emit("disconnected", {});
    });

    this.socket.on("connect_error", (error) => {
      console.error("[InteractiveVoice] Connection error:", error);
      this.emit("error", { message: "Connection failed", error });
    });

    this.socket.on("contentStart", (data) => {
      if (data.type === "AUDIO" && data.role === "ASSISTANT") {
        this.emit("aiSpeakingChange", true);
      }
    });

    this.socket.on("audioOutput", (data) => {
      if (data.content) {
        try {
          const binaryString = atob(data.content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          this.audioQueue.push(bytes.buffer);
          this.playAudioFromQueue();
        } catch (err) {
          console.error("[InteractiveVoice] Audio decode error:", err);
        }
      }
    });

    this.socket.on("textOutput", (data) => {
      if (data.content) {
        const content = data.content.trim();

        // Check for interrupt signal
        if (content.startsWith("{") && content.endsWith("}")) {
          try {
            const jsonData = JSON.parse(content);
            if (jsonData.interrupted === true) {
              console.log("[InteractiveVoice] Interrupt detected");
              this.stopAllAudio();
            }
          } catch (e) {}
          return;
        }

        if (content.length < 2) return;

        const message = {
          id: Date.now() + Math.random(),
          role: data.role === "USER" ? "user" : "assistant",
          text: content,
          timestamp: new Date().toISOString(),
        };

        this.emit("transcript", message);
      }
    });

    this.socket.on("contentEnd", (data) => {
      if (data.type === "AUDIO") {
        this.emit("aiSpeakingChange", false);
      }
    });
  }

  /**
   * Start audio capture from microphone
   */
  startAudioCapture() {
    if (!this.mediaStream || !this.audioContext) return;

    try {
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      const processor = this.audioContext.createScriptProcessor(2048, 1, 1);

      processor.onaudioprocess = (e) => {
        if (!this.socket?.connected || !this.isActive) return;

        const inputData = e.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < inputData.length; i++) {
          sum += inputData[i] * inputData[i];
        }
        const level = Math.sqrt(sum / inputData.length) * 10;
        const normalizedLevel = Math.min(1, level);
        const isSpeaking = level > 0.02;

        this.emit("audioLevel", normalizedLevel);
        this.emit("userSpeakingChange", isSpeaking);

        const pcm16Data = this.float32ToInt16(inputData);
        const uint8Array = new Uint8Array(pcm16Data.buffer);
        const base64 = btoa(String.fromCharCode.apply(null, uint8Array));
        this.socket.emit("audioInput", base64);
      };

      source.connect(processor);
      processor.connect(this.audioContext.destination);
      this.processor = processor;
    } catch (err) {
      console.error("[InteractiveVoice] Audio capture error:", err);
      this.emit("error", { message: "Failed to capture audio", error: err });
    }
  }

  /**
   * Connect to the voice service (browser-based)
   */
  async connect(systemPrompt, provider = "AWS") {
    this.provider = provider;
    const providerConfig = PROVIDERS[provider];

    if (!providerConfig || providerConfig.disabled) {
      throw new Error(`Provider ${provider} is not available`);
    }

    if (!providerConfig.proxyUrl) {
      throw new Error(`Provider ${provider} proxy URL not configured`);
    }

    try {
      // Initialize audio context
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });

      // Get microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Connect to Socket.IO server
      this.socket = io(providerConfig.proxyUrl, {
        transports: ["websocket", "polling"],
        reconnection: true,
        autoConnect: false,
      });

      this.setupSocketListeners();
      this.socket.connect();

      // Wait for connection
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error("Connection timeout")), 10000);
        if (this.socket.connected) {
          clearTimeout(timeout);
          resolve();
        } else {
          this.socket.once("connect", () => {
            clearTimeout(timeout);
            resolve();
          });
          this.socket.once("connect_error", () => {
            clearTimeout(timeout);
            reject(new Error("Connection failed"));
          });
        }
      });

      // Initialize connection
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error("Init timeout")), 5000);
        this.socket.emit("initializeConnection", (ack) => {
          clearTimeout(timeout);
          if (ack?.success) resolve();
          else reject(new Error(ack?.error || "Failed to initialize"));
        });
      });

      // Send system prompt
      this.socket.emit("promptStart");
      this.socket.emit("systemPrompt", systemPrompt || DEFAULT_PROMPTS.realtime);

      // Start audio
      await new Promise((resolve) => {
        this.socket.once("audioReady", resolve);
        this.socket.emit("audioStart");
        setTimeout(resolve, 500);
      });

      this.isActive = true;
      this.startAudioCapture();

      return { success: true, provider: providerConfig.name };
    } catch (err) {
      console.error("[InteractiveVoice] Session error:", err);
      this.disconnect();
      throw err;
    }
  }

  /**
   * Initiate a phone call (via backend)
   */
  async initiatePhoneCall(params) {
    const { phoneNumber, mode, voiceId, systemPrompt, voicemailScript } = params;
    const providerConfig = PROVIDERS[this.provider];

    if (!providerConfig || !providerConfig.phoneUrl) {
      throw new Error(`Phone calls not available for provider ${this.provider}`);
    }

    const response = await fetch(`${providerConfig.phoneUrl}/initiate-call`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phoneNumber,
        mode,
        voiceId,
        systemPrompt,
        voicemailScript,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to initiate call");
    }

    return data;
  }

  /**
   * Get call status (for phone calls)
   */
  async getCallStatus(callSid) {
    const providerConfig = PROVIDERS[this.provider];

    if (!providerConfig || !providerConfig.phoneUrl) {
      throw new Error(`Call status not available for provider ${this.provider}`);
    }

    const response = await fetch(`${providerConfig.phoneUrl}/call-status/${callSid}`);
    
    if (!response.ok) {
      throw new Error("Failed to get call status");
    }

    return response.json();
  }

  /**
   * End a phone call
   */
  async endPhoneCall(callSid) {
    const providerConfig = PROVIDERS[this.provider];

    if (!providerConfig || !providerConfig.phoneUrl) {
      throw new Error(`End call not available for provider ${this.provider}`);
    }

    await fetch(`${providerConfig.phoneUrl}/end-call/${callSid}`, { method: "POST" });
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    this.isActive = false;
    this.stopAllAudio();

    if (this.socket?.connected) {
      this.socket.emit("stopAudio");
      this.socket.disconnect();
      this.socket.removeAllListeners();
    }
    this.socket = null;

    if (this.processor) {
      this.processor.disconnect();
    }
    this.processor = null;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
    }
    this.mediaStream = null;

    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
    }
    this.audioContext = null;

    this.emit("disconnected", {});
  }
}

export default InteractiveVoiceService;
export { PROVIDERS, DEFAULT_PROMPTS };
